import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';
import ClinicLayout from './components/Layout/ClinicLayout';
import SuperAdminLayout from './components/Layout/SuperAdminLayout';
import ConfigurationCheck from './components/common/ConfigurationCheck';
import SiteCompanyNotice from './components/legal/SiteCompanyNotice';
import './App.css';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';

// Static imports for public marketing pages (required for react-snap prerendering)
import LandingPage from './pages/public/LandingPage';
import HomepageV2 from './pages/public/HomepageV2';
import DentalConsentForms from './pages/public/DentalConsentForms';
import DentalConsentPlans from './pages/public/DentalConsentPlans';
import DentalComplianceSoftware from './pages/public/DentalComplianceSoftware';
import DigitalConsentDentists from './pages/public/DigitalConsentDentists';
import DentalPracticeManagement from './pages/public/DentalPracticeManagement';
import DentalTemplates from './pages/public/DentalTemplates';
import DentalConsentSoftware from './pages/public/DentalConsentSoftware';
import DentalGoogleReviews from './pages/public/DentalGoogleReviews';
import DentalConsentManagement from './pages/public/DentalConsentManagement';
import InsightsCMAConsent from './pages/public/InsightsCMAConsent';
import DentalTriagePage from './pages/public/DentalTriagePage';
import CPDLegalConsentPage from './pages/public/CPDLegalConsentPage';
import NhsDentalContractCalculators from './pages/public/NhsDentalContractCalculators';
import BookDemo from './pages/public/BookDemo';
import DentalConsentLawUk from './pages/public/DentalConsentLawUk';
import NhsMixedTreatmentCompliance from './pages/public/NhsMixedTreatmentCompliance';
import DentalConsentGuide from './pages/public/DentalConsentGuide';
import ImplantConsentFormUk from './pages/public/ImplantConsentFormUk';
import OrthodonticConsentFormUk from './pages/public/OrthodonticConsentFormUk';
import ToothExtractionConsentFormUk from './pages/public/ToothExtractionConsentFormUk';
import RootCanalConsentFormUk from './pages/public/RootCanalConsentFormUk';
import CompositeBondingConsentFormUk from './pages/public/CompositeBondingConsentFormUk';
import VeneerConsentFormUk from './pages/public/VeneerConsentFormUk';
import WisdomToothConsentFormUk from './pages/public/WisdomToothConsentFormUk';
import SdcepInfectiveEndocarditisProphylaxis from './pages/public/SdcepInfectiveEndocarditisProphylaxis';
import SedationConsentFormUk from './pages/public/SedationConsentFormUk';
import SupportPage from './pages/public/SupportPage';
import Terms from './pages/public/Terms';
import Privacy from './pages/public/Privacy';
import Contact from './pages/public/Contact';

// Lazy load app, auth, and patient-facing routes
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const EmailVerifiedPage = lazy(() => import('./pages/auth/EmailVerifiedPage'));
const SelectPlanPage = lazy(() => import('./pages/auth/SelectPlanPage'));
const CompleteRegistrationPage = lazy(() => import('./pages/auth/CompleteRegistrationPage'));
const AuthActionRedirect = lazy(() => import('./pages/auth/AuthActionRedirect'));
const SuperAdminLoginPage = lazy(() => import('./pages/auth/SuperAdminLoginPage'));
const Dashboard = lazy(() => import('./pages/clinic/Dashboard'));
const Patients = lazy(() => import('./pages/clinic/Patients'));
const Consents = lazy(() => import('./pages/clinic/Consents'));
const ConsentDetailPage = lazy(() => import('./pages/clinic/ConsentDetailPage'));
const Treatments = lazy(() => import('./pages/clinic/Treatments'));
const Settings = lazy(() => import('./pages/clinic/Settings'));
const Staff = lazy(() => import('./pages/clinic/Staff'));
const StaffRegistration = lazy(() => import('./pages/clinic/StaffRegistration'));
const Billing = lazy(() => import('./pages/clinic/Billing'));
const EditPatient = lazy(() => import('./pages/clinic/EditPatient'));
const ViewPatient = lazy(() => import('./pages/clinic/ViewPatient'));
const UserCleanup = lazy(() => import('./pages/clinic/UserCleanup'));
const CQCPage = lazy(() => import('./pages/cqc/CQCPage'));
const SimpleConsentForm = lazy(() => import('./components/consents/SimpleConsentForm'));
const SuperAdminDashboard = lazy(() => import('./pages/superadmin/Dashboard'));
const Clinics = lazy(() => import('./pages/superadmin/Clinics'));
const UserExportPage = lazy(() => import('./pages/superadmin/UserExportPage'));
const ClinicView = lazy(() => import('./pages/superadmin/ClinicView'));
const CreateClinicForm = lazy(() => import('./components/clinics/CreateClinicForm'));
const EditPracticeForm = lazy(() => import('./pages/superadmin/EditPracticeForm'));
const AllConsents = lazy(() => import('./pages/superadmin/AllConsents'));
const ConsentView = lazy(() => import('./pages/superadmin/ConsentView'));
const ClinicConsents = lazy(() => import('./pages/superadmin/ClinicConsents'));
const TreatmentVideosPage = lazy(() => import('./pages/superadmin/TreatmentVideosPage'));
const RegistrationFunnelPage = lazy(() => import('./pages/superadmin/RegistrationFunnelPage'));
const DentalTriageLeadsPage = lazy(() => import('./pages/superadmin/DentalTriageLeadsPage'));
const ClinicEngagementPage = lazy(() => import('./pages/superadmin/ClinicEngagementPage'));
const DemoInviteAuditPage = lazy(() => import('./pages/superadmin/DemoInviteAuditPage'));
const Analytics = lazy(() => import('./pages/superadmin/Analytics'));
const WebsiteTrafficPage = lazy(() => import('./pages/superadmin/WebsiteTrafficPage'));
const Revenue = lazy(() => import('./pages/superadmin/Revenue'));
const Logs = lazy(() => import('./pages/superadmin/Logs'));
const PracticeDocuments = lazy(() => import('./pages/superadmin/PracticeDocuments'));
const SuperadminProfile = lazy(() => import('./pages/superadmin/SuperadminProfile'));
const SupportTickets = lazy(() => import('./pages/superadmin/SupportTickets'));
const PatientConsentForm = lazy(() => import('./pages/public/PatientConsentForm'));
const AIConsentSigningPage = lazy(() => import('./pages/public/AIConsentSigningPage'));
const ConsentPdfView = lazy(() => import('./pages/public/ConsentPdfView'));
const DOBVerification = lazy(() => import('./components/consents/DOBVerification'));

const PatientForm = lazy(() => import('./components/patients/PatientForm'));
const AnalyticsPage = lazy(() => import('./pages/clinic/Analytics'));
const MyAccount = lazy(() => import('./pages/clinic/MyAccount'));
const ClinicMessages = lazy(() => import('./pages/clinic/Messages'));
const PracticeReviewPage = lazy(() => import('./pages/public/PracticeReviewPage'));
const GoogleReviewsPage = lazy(() => import('./pages/clinic/GoogleReviewsPage'));
const ReviewOutreachPage = lazy(() => import('./pages/clinic/ReviewOutreachPage'));

// Setup pages
const TestSetup = lazy(() => import('./pages/setup/TestSetup'));
const FixUserAccount = lazy(() => import('./pages/setup/FixUserAccount'));
const ReactivateSuperAdmin = lazy(() => import('./pages/setup/ReactivateSuperAdmin'));
const UpdateTemplateSubtypes = lazy(() => import('./pages/setup/UpdateTemplateSubtypes'));
const VideoConsentDemo = lazy(() => import('./pages/setup/VideoConsentDemo'));
const VideoCostComparison = lazy(() => import('./pages/setup/VideoCostComparison'));
const TestPatientCreation = lazy(() => import('./pages/setup/TestPatientCreation'));
const ClinicianSignConsent = lazy(() => import('./pages/clinic/ClinicianSignConsent'));

// Loading component for Suspense
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner size="large" />
  </div>
);

function LegacyHomepage() {
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <LandingPage />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomepageV2 />} />
            <Route path="/homepage-v2" element={<Navigate to="/" replace />} />
            <Route path="/homepage-legacy" element={<LegacyHomepage />} />
            <Route path="/dental-consent-forms" element={<DentalConsentForms />} />
            <Route path="/dental-consent-plans" element={<DentalConsentPlans />} />
            <Route path="/dental-compliance-software" element={<DentalComplianceSoftware />} />
            <Route path="/digital-consent-dentists" element={<DigitalConsentDentists />} />
      <Route path="/dental-practice-management" element={<DentalPracticeManagement />} />
      <Route path="/dental-templates" element={<DentalTemplates />} />
      <Route path="/dental-consent-software" element={<DentalConsentSoftware />} />
            <Route path="/dental-google-reviews" element={<DentalGoogleReviews />} />
            <Route path="/dental-consent-management" element={<DentalConsentManagement />} />
            <Route path="/insights/consent-is-coming" element={<InsightsCMAConsent />} />
            <Route path="/dentaltriage" element={<DentalTriagePage />} />
            <Route path="/cpd-legal-consent" element={<CPDLegalConsentPage />} />
            <Route path="/nhs-dental-contract-calculators" element={<NhsDentalContractCalculators />} />
            <Route path="/book-demo" element={<BookDemo />} />
            <Route path="/dental-consent-law-uk" element={<DentalConsentLawUk />} />
            <Route path="/nhs-mixed-treatment-compliance" element={<NhsMixedTreatmentCompliance />} />
            <Route path="/dental-consent-guide" element={<DentalConsentGuide />} />
            <Route path="/implant-consent-form-uk" element={<ImplantConsentFormUk />} />
            <Route path="/orthodontic-consent-form-uk" element={<OrthodonticConsentFormUk />} />
            <Route path="/tooth-extraction-consent-form-uk" element={<ToothExtractionConsentFormUk />} />
            <Route path="/root-canal-consent-form-uk" element={<RootCanalConsentFormUk />} />
            <Route path="/composite-bonding-consent-form-uk" element={<CompositeBondingConsentFormUk />} />
            <Route path="/veneer-consent-form-uk" element={<VeneerConsentFormUk />} />
            <Route path="/wisdom-tooth-consent-form-uk" element={<WisdomToothConsentFormUk />} />
            <Route
              path="/sdcep-infective-endocarditis-prophylaxis"
              element={<SdcepInfectiveEndocarditisProphylaxis />}
            />
            <Route path="/sedation-consent-form-uk" element={<SedationConsentFormUk />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
            <Route path="/auth/email-verified" element={<EmailVerifiedPage />} />
            <Route path="/auth/select-plan" element={<SelectPlanPage />} />
            <Route path="/auth/complete-registration" element={<CompleteRegistrationPage />} />
            {/* Firebase sometimes lands on /__/auth/action — forward verify links into our funnel */}
            <Route
              path="/__/auth/action"
              element={<AuthActionRedirect />}
            />
            <Route path="/admin-login" element={<SuperAdminLoginPage />} />
            <Route path="/consent/:consentId" element={<PatientConsentForm />} />
            <Route path="/ai-consent/:consentId" element={<AIConsentSigningPage />} />
            <Route path="/consent-pdf/:consentId" element={<ConsentPdfView />} />
            <Route path="/view-consent/:accessToken" element={<ConsentPdfView />} />
            <Route path="/verify-dob/:accessToken" element={<DOBVerification />} />
            <Route path="/sign-consent/:accessToken" element={<AIConsentSigningPage />} />
            <Route path="/review/:clinicId" element={<PracticeReviewPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Clinic Routes */}
            <Route path="/clinic" element={<PrivateRoute><ClinicLayout><Dashboard /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/patients" element={<PrivateRoute><ClinicLayout><Patients /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/consents" element={<PrivateRoute requiredPermissions={['createConsents']}><ClinicLayout><Consents /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/consents/:consentId" element={<PrivateRoute requiredPermissions={['createConsents']}><ClinicLayout><ConsentDetailPage /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/consents/ai-generate" element={<PrivateRoute requiredPermissions={['createConsents']}><ClinicLayout><SimpleConsentForm /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/templates" element={<Navigate to="/clinic/consents" replace />} />
            <Route path="/clinic/cqc" element={<PrivateRoute requiredPermissions={['accessSettings']}><ClinicLayout><CQCPage /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/treatments" element={<PrivateRoute><ClinicLayout><Treatments /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/settings" element={<PrivateRoute requiredPermissions={['accessSettings']}><ClinicLayout><Settings /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/staff" element={<PrivateRoute requiredPermissions={['manageStaff']}><ClinicLayout><Staff /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/staff/register" element={<PrivateRoute requiredPermissions={['manageStaff']}><ClinicLayout><StaffRegistration /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/staff-registration" element={<PrivateRoute requiredPermissions={['manageStaff']}><ClinicLayout><StaffRegistration /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/billing" element={<PrivateRoute><ClinicLayout><Billing /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/patients/:patientId/edit" element={<PrivateRoute><ClinicLayout><EditPatient /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/patients/:patientId" element={<PrivateRoute><ClinicLayout><ViewPatient /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/user-cleanup" element={<PrivateRoute requiredPermissions={['manageStaff']}><ClinicLayout><UserCleanup /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/patients/new" element={<PrivateRoute><ClinicLayout><PatientForm /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/analytics" element={<PrivateRoute requiredPermissions={['viewStats']}><ClinicLayout><AnalyticsPage /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/account" element={<PrivateRoute><ClinicLayout><MyAccount /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/messages" element={<PrivateRoute><ClinicLayout><ClinicMessages /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/reviews" element={<PrivateRoute requiredPermissions={['accessSettings']}><ClinicLayout><GoogleReviewsPage /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/review-list" element={<PrivateRoute requiredPermissions={['accessSettings']}><ClinicLayout><ReviewOutreachPage /></ClinicLayout></PrivateRoute>} />
            <Route path="/clinic/reviews/outreach" element={<Navigate to="/clinic/review-list" replace />} />
            <Route path="/clinic/sign-consent/:consentId" element={<PrivateRoute requiredPermissions={['finaliseConsents']}><ClinicLayout><ClinicianSignConsent /></ClinicLayout></PrivateRoute>} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Super Admin Routes - Protected with role check */}
            <Route path="/admin" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><SuperAdminDashboard /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/clinics" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><Clinics /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/user-export" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><UserExportPage /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/clinics/new" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><CreateClinicForm /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/clinics/:clinicId" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><ClinicView /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/clinics/:clinicId/edit" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><EditPracticeForm /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/consents" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><AllConsents /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/consents/:consentId" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><ConsentView /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/clinics/:clinicId/consents" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><ClinicConsents /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/treatment-videos" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><TreatmentVideosPage /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/registration-funnel" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><RegistrationFunnelPage /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/dental-triage" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><DentalTriageLeadsPage /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/clinic-engagement" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><ClinicEngagementPage /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/demo-invites" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><DemoInviteAuditPage /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/consent-templates" element={<Navigate to="/admin/treatment-videos" replace />} />
            <Route path="/admin/consent-templates/*" element={<Navigate to="/admin/treatment-videos" replace />} />
            <Route path="/admin/analytics" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><Analytics /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/website-traffic" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><WebsiteTrafficPage /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/revenue" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><Revenue /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/logs" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><Logs /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/documents" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><PracticeDocuments /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/profile" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><SuperadminProfile /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/support" element={<PrivateRoute allowedRoles={['super_admin']}><SuperAdminLayout><SupportTickets /></SuperAdminLayout></PrivateRoute>} />
            <Route path="/admin/consents/new" element={<Navigate to="/admin/treatment-videos" replace />} />
            <Route path="/templates/consent" element={<Navigate to="/admin/treatment-videos" replace />} />
            <Route path="/templates/consent/*" element={<Navigate to="/admin/treatment-videos" replace />} />
            <Route path="/test-consent-form" element={<Navigate to="/admin/treatment-videos" replace />} />

            {/* Setup Routes */}
            <Route path="/setup/test" element={<TestSetup />} />
            <Route path="/setup/fix-user" element={<FixUserAccount />} />
            <Route path="/setup/reactivate-superadmin" element={<ReactivateSuperAdmin />} />
            <Route path="/setup/update-templates" element={<UpdateTemplateSubtypes />} />
            <Route path="/setup/video-consent-demo" element={<VideoConsentDemo />} />
            <Route path="/setup/video-cost-comparison" element={<VideoCostComparison />} />
            <Route path="/setup/test-patient-creation" element={<TestPatientCreation />} />

            {/* Catch all route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
          <SiteCompanyNotice />
          <ConfigurationCheck />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;